/** @file clip.shiftFrames: moves the keys (with their events) and track entries of a frame range. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { checkFrame } from './checkFrame';
import { shiftTrack } from './shiftTrack';

/** Moves the keys of [from, to] by `by`; out of range → `FRAME_OUT_OF_RANGE`, onto an unmoved key → `VALIDATION`. */
function shiftKeys(clip: Clip, from: number, to: number, by: number): number {
  const moving = (f: number): boolean => f >= from && f <= to;
  const still = new Set(clip.keys.filter((k) => !moving(k.frame)).map((k) => k.frame));
  let moved = 0;
  clip.keys.forEach((k, i) => {
    if (!moving(k.frame)) return;
    checkFrame(clip, k.frame + by, `/clips/${clip.name}/keys/${i}/frame`);
    if (still.has(k.frame + by)) throwError(ERROR_CODES.VALIDATION, { frame: k.frame, to: k.frame + by }, { path: '/input/by', hint: `frame ${k.frame + by} has a key that does not move` });
    k.frame += by;
    moved++;
  });
  clip.keys.sort((a, b) => a.frame - b.frame);
  return moved;
}

/**
 * `clip.shiftFrames { clip, from, by, to? }` → `{ moved }`: keys in [from, to ?? frames] move by `by` with their
 * events; every track goes through `shiftTrack`; `loopStart` and `start` stay. Inverse C.
 */
export const clipShiftFramesCommand: CommandDef<'clip.shiftFrames'> = {
  name: 'clip.shiftFrames',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'from', 'by', 'to']);
    return compact({ clip: r.str('clip'), from: r.int('from', { min: 0 }), by: r.int('by'), to: r.opt('to', (k) => r.int(k, { min: 0 })) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const inverse = [clipInverse(clip)];
    const to = input.to ?? clip.frames;
    const moved = shiftKeys(clip, input.from, to, input.by);
    if (clip.tracks !== undefined) {
      clip.tracks = Object.fromEntries(Object.entries(clip.tracks).map(([n, t]) => [n, shiftTrack(t, input.from, to, input.by)]));
    }
    checkClip(name, clip);
    return { result: { moved }, warnings: [], inverse };
  },
};
